/**
 * Analytics Service
 *
 * Typed singleton wrapper around mixpanel-browser for custom event tracking.
 * All calls are guarded by isAnalyticsEnabled() and wrapped in try-catch;
 * `track` also honours staging capture mode (see analytics-mode.ts).
 * Analytics failures never break the application.
 *
 * Event names use GA4 standard names (snake_case).
 *
 * This module never imports the SDK itself (type-only import below). The
 * customer layout and most pages import it, so a value import put the whole
 * ≈430KB mixpanel-browser chunk on every signed-in page's critical path. The
 * lazily loaded MixpanelInit owns the SDK and hands the initialised instance
 * over in its `loaded` callback; until then calls are no-ops (tracks queue),
 * exactly as the uninitialised SDK dropped them before.
 */

import type { OverridedMixpanel } from 'mixpanel-browser'

import { isAnalyticsExcludedPath } from '@/components/molecules/shared/mixpanel-guards'
import { isAnalyticsEnabled } from '@/config'

import { analyticsMode, recordMixpanelCapture } from './analytics-mode'
import type { AnalyticsEventMap } from './analytics.types'
import { scrubAnalyticsUrl } from './safe-url'

/**
 * mixpanel-browser drops (throws inside its own try/catch) a `track` made
 * before `mixpanel.init`, and MixpanelInit loads lazily, so the first events
 * of a fresh document (email `sign_up`, the first `onboarding_step_viewed`)
 * raced it and were lost. Live-mode tracks made before Mixpanel is loaded wait
 * here, bounded so a page that never initialises cannot grow the queue, and
 * MixpanelInit's `loaded` callback flushes them in order. A track made on an
 * excluded (credential-bearing) path is never queued: MixpanelInit does not
 * load there, and a later tracked page must not send it.
 *
 * MixpanelInit now mounts only after the page's first data, so a queued track
 * is stamped when it is made: `time` (epoch seconds, the SDK's own unit) and
 * `$current_url`, which the SDK would otherwise fill in at send time with
 * whatever page the customer has moved on to. Caller properties win over
 * the SDK's defaults, so the stamps are what Mixpanel records.
 */
const MAX_QUEUED_MIXPANEL_TRACKS = 50
/** The part of the SDK this service calls. */
export type MixpanelClient = Pick<OverridedMixpanel, 'track' | 'identify' | 'register' | 'people'>

let mixpanel: MixpanelClient | null = null
let queuedMixpanelTracks: Array<[string, Record<string, unknown>]> = []

function sendToMixpanel(client: MixpanelClient, eventName: string, properties: Record<string, unknown>): void {
  try {
    client.track(eventName, properties)
  } catch {
    // Analytics must never break the app
  }
}

/** Test seam: back to a document where Mixpanel has not loaded yet. */
export function _resetMixpanelTrackQueueForTests(): void {
  mixpanel = null
  queuedMixpanelTracks = []
}

class AnalyticsService {
  // ─── Core Methods ─────────────────────────────────────────────

  /** Called from MixpanelInit's `loaded` callback with the initialised SDK: sends every queued track, in order. */
  mixpanelLoaded(client: MixpanelClient): void {
    mixpanel = client
    const queued = queuedMixpanelTracks
    queuedMixpanelTracks = []
    for (const [eventName, properties] of queued) sendToMixpanel(client, eventName, properties)
  }

  track<K extends keyof AnalyticsEventMap>(eventName: K, properties: AnalyticsEventMap[K]): void {
    const mode = analyticsMode()
    if (mode === 'off') return
    if (mode === 'capture') {
      recordMixpanelCapture(eventName as string, properties)
      return
    }
    const props = properties as unknown as Record<string, unknown>
    if (!mixpanel) {
      const onExcludedPath = typeof window !== 'undefined' && isAnalyticsExcludedPath(window.location?.pathname)
      if (!onExcludedPath && queuedMixpanelTracks.length < MAX_QUEUED_MIXPANEL_TRACKS) {
        const href = typeof window === 'undefined' ? undefined : window.location?.href
        const currentUrl = href ? scrubAnalyticsUrl(href) : undefined
        queuedMixpanelTracks.push([
          eventName as string,
          { ...props, time: Date.now() / 1000, ...(currentUrl ? { $current_url: currentUrl } : {}) },
        ])
      }
      return
    }
    sendToMixpanel(mixpanel, eventName as string, props)
  }

  identify(userId: string): void {
    if (!isAnalyticsEnabled()) return
    try {
      mixpanel?.identify(userId)
    } catch {
      // Analytics must never break the app
    }
  }

  registerSuperProperties(properties: Record<string, unknown>): void {
    if (!isAnalyticsEnabled()) return
    try {
      mixpanel?.register(properties)
    } catch {
      // Analytics must never break the app
    }
  }

  setUserProperties(properties: Record<string, unknown>): void {
    if (!isAnalyticsEnabled()) return
    try {
      mixpanel?.people.set(properties)
    } catch {
      // Analytics must never break the app
    }
  }

  setUserPropertiesOnce(properties: Record<string, unknown>): void {
    if (!isAnalyticsEnabled()) return
    try {
      mixpanel?.people.set_once(properties)
    } catch {
      // Analytics must never break the app
    }
  }

  incrementUserProperty(property: string, value: number = 1): void {
    if (!isAnalyticsEnabled()) return
    try {
      mixpanel?.people.increment(property, value)
    } catch {
      // Analytics must never break the app
    }
  }

  // ─── Pages ────────────────────────────────────────────────────

  pageViewed(props: AnalyticsEventMap['page_view']): void {
    this.track('page_view', props)
  }

  // ─── Onboarding ───────────────────────────────────────────────

  onboardingStarted(props: AnalyticsEventMap['sign_up_start']): void {
    this.track('sign_up_start', props)
  }

  onboardingTypeSelected(props: AnalyticsEventMap['onboarding_type_selected']): void {
    this.track('onboarding_type_selected', props)
  }

  onboardingStepViewed(props: AnalyticsEventMap['onboarding_step_viewed']): void {
    this.track('onboarding_step_viewed', props)
  }

  onboardingStepCompleted(props: AnalyticsEventMap['onboarding_step_completed']): void {
    this.track('onboarding_step_completed', props)
  }

  companyStructureSelected(props: AnalyticsEventMap['company_structure_selected']): void {
    this.track('company_structure_selected', props)
  }

  shareholderAdded(props: AnalyticsEventMap['shareholder_added']): void {
    this.track('shareholder_added', props)
  }

  shareholderRemoved(props: AnalyticsEventMap['shareholder_removed']): void {
    this.track('shareholder_removed', props)
  }

  // Package Viewed → view_item_list (item_list_name: 'packages')
  packageViewed(props: AnalyticsEventMap['view_item_list']): void {
    this.track('view_item_list', { ...props, item_list_name: 'packages' })
  }

  // Package Selected → select_item
  packageSelected(props: AnalyticsEventMap['select_item']): void {
    this.track('select_item', props)
  }

  // Checkout Started → begin_checkout (item_category: 'formation')
  checkoutStarted(props: AnalyticsEventMap['begin_checkout']): void {
    this.track('begin_checkout', { ...props, item_category: props.item_category || 'formation' })
  }

  documentUploaded(props: AnalyticsEventMap['document_uploaded']): void {
    this.track('document_uploaded', props)
  }

  // The counterpart to documentUploaded. Without it an upload path that is
  // failing for everyone is indistinguishable from customers deciding not to
  // upload: both show up as an absence of `document_uploaded`.
  documentUploadFailed(props: AnalyticsEventMap['document_upload_failed']): void {
    this.track('document_upload_failed', props)
  }

  einExtracted(props: AnalyticsEventMap['ein_extracted']): void {
    this.track('ein_extracted', props)
  }

  // Onboarding Completed → sign_up
  onboardingCompleted(props: AnalyticsEventMap['sign_up']): void {
    this.track('sign_up', props)
  }

  // ─── Dashboard ────────────────────────────────────────────────

  dashboardViewed(props: AnalyticsEventMap['view_dashboard']): void {
    this.track('view_dashboard', props)
  }

  formationProgressViewed(props: AnalyticsEventMap['formation_progress_viewed']): void {
    this.track('formation_progress_viewed', props)
  }

  quickActionClicked(props: AnalyticsEventMap['quick_action_clicked']): void {
    this.track('quick_action_clicked', props)
  }

  deadlineCardViewed(props: AnalyticsEventMap['deadline_card_viewed']): void {
    this.track('deadline_card_viewed', props)
  }

  // ─── Services ─────────────────────────────────────────────────

  // Services Page Viewed → view_item_list (item_list_name: 'services')
  servicesPageViewed(props: AnalyticsEventMap['view_item_list']): void {
    this.track('view_item_list', { ...props, item_list_name: 'services' })
  }

  // Service Card Viewed → view_item (content_type: 'service')
  serviceCardViewed(props: AnalyticsEventMap['view_item']): void {
    this.track('view_item', { ...props, content_type: 'service' })
  }

  // Service Learn More Clicked → select_content (content_type: 'service_learn_more')
  serviceLearnMoreClicked(props: AnalyticsEventMap['select_content']): void {
    this.track('select_content', { ...props, content_type: 'service_learn_more' })
  }

  // Service Purchase Started → begin_checkout (item_category: 'service')
  servicePurchaseStarted(props: AnalyticsEventMap['begin_checkout']): void {
    this.track('begin_checkout', { ...props, item_category: 'service' })
  }

  // ─── Federal Tax ──────────────────────────────────────────────

  federalTaxListViewed(props: AnalyticsEventMap['federal_tax_list_viewed']): void {
    this.track('federal_tax_list_viewed', props)
  }

  federalTaxRepresentativeNoticeViewed(props: AnalyticsEventMap['federal_tax_representative_notice_viewed']): void {
    this.track('federal_tax_representative_notice_viewed', props)
  }

  federalTaxRepresentativeNoticeAccepted(props: AnalyticsEventMap['federal_tax_representative_notice_accepted']): void {
    this.track('federal_tax_representative_notice_accepted', props)
  }

  federalTaxFilingStarted(props: AnalyticsEventMap['federal_tax_filing_started']): void {
    this.track('federal_tax_filing_started', props)
  }

  federalTaxStepViewed(props: AnalyticsEventMap['federal_tax_step_viewed']): void {
    this.track('federal_tax_step_viewed', props)
  }

  federalTaxStepCompleted(props: AnalyticsEventMap['federal_tax_step_completed']): void {
    this.track('federal_tax_step_completed', props)
  }

  federalTaxPartnerAdded(props: AnalyticsEventMap['federal_tax_partner_added']): void {
    this.track('federal_tax_partner_added', props)
  }

  federalTaxFilingSubmitted(props: AnalyticsEventMap['federal_tax_filing_submitted']): void {
    this.track('federal_tax_filing_submitted', props)
  }

  // Federal Tax Purchase Started → begin_checkout (item_category: 'federal_tax')
  federalTaxPurchaseStarted(props: AnalyticsEventMap['begin_checkout']): void {
    this.track('begin_checkout', { ...props, item_category: 'federal_tax' })
  }

  // ─── State Tax ────────────────────────────────────────────────

  stateTaxPageViewed(props: AnalyticsEventMap['state_tax_page_viewed']): void {
    this.track('state_tax_page_viewed', props)
  }

  stateTaxFilingStarted(props: AnalyticsEventMap['state_tax_filing_started']): void {
    this.track('state_tax_filing_started', props)
  }

  stateTaxSelfFiled(props: AnalyticsEventMap['state_tax_self_filed']): void {
    this.track('state_tax_self_filed', props)
  }

  stateTaxSelfFiledDeleted(props: AnalyticsEventMap['state_tax_self_filed_deleted']): void {
    this.track('state_tax_self_filed_deleted', props)
  }

  stateTaxFilingDetailViewed(props: AnalyticsEventMap['state_tax_filing_detail_viewed']): void {
    this.track('state_tax_filing_detail_viewed', props)
  }

  // ─── Mailroom ─────────────────────────────────────────────────

  mailroomViewed(props: AnalyticsEventMap['mailroom_viewed']): void {
    this.track('mailroom_viewed', props)
  }

  mailItemViewed(props: AnalyticsEventMap['mail_item_viewed']): void {
    this.track('mail_item_viewed', props)
  }

  // Mail Document Downloaded → file_download
  mailDocumentDownloaded(props: AnalyticsEventMap['file_download']): void {
    this.track('file_download', props)
  }

  mailroomAddressCopied(props: AnalyticsEventMap['mailroom_address_copied']): void {
    this.track('mailroom_address_copied', props)
  }

  // Mailroom Purchase Started → begin_checkout (item_category: 'mailroom')
  mailroomPurchaseStarted(props: AnalyticsEventMap['begin_checkout']): void {
    this.track('begin_checkout', { ...props, item_category: 'mailroom' })
  }

  // ─── Company Profile ─────────────────────────────────────────

  companyProfileViewed(props: AnalyticsEventMap['company_profile_viewed']): void {
    this.track('company_profile_viewed', props)
  }

  companyTabChanged(props: AnalyticsEventMap['company_tab_changed']): void {
    this.track('company_tab_changed', props)
  }

  // Company Document Viewed → view_item (content_type: 'document')
  companyDocumentViewed(props: AnalyticsEventMap['view_item']): void {
    this.track('view_item', { ...props, content_type: 'document' })
  }

  // Company Document Uploaded → document_uploaded
  companyDocumentUploaded(props: AnalyticsEventMap['document_uploaded']): void {
    this.track('document_uploaded', props)
  }

  // ─── Banking & Other ─────────────────────────────────────────

  bankingPageViewed(props: AnalyticsEventMap['banking_page_viewed']): void {
    this.track('banking_page_viewed', props)
  }

  // Bank Partner Clicked → select_content (content_type: 'bank_partner')
  bankPartnerClicked(props: AnalyticsEventMap['select_content']): void {
    this.track('select_content', { ...props, content_type: 'bank_partner' })
  }

  accountingPageViewed(props: AnalyticsEventMap['accounting_page_viewed']): void {
    this.track('accounting_page_viewed', props)
  }

  connectionsPageViewed(props: AnalyticsEventMap['connections_page_viewed']): void {
    this.track('connections_page_viewed', props)
  }

  // ─── Bookkeeping Questions ───────────────────────────────────

  accountingQuestionsViewed(props: AnalyticsEventMap['accounting_questions_viewed']): void {
    this.track('accounting_questions_viewed', props)
  }

  accountingQuestionAnswered(props: AnalyticsEventMap['accounting_question_answered']): void {
    this.track('accounting_question_answered', props)
  }

  accountingQuestionAnswerFailed(props: AnalyticsEventMap['accounting_question_answer_failed']): void {
    this.track('accounting_question_answer_failed', props)
  }

  federalTaxMembersConfirmed(props: AnalyticsEventMap['federal_tax_members_confirmed']): void {
    this.track('federal_tax_members_confirmed', props)
  }

  federalTaxMemberAdded(props: AnalyticsEventMap['federal_tax_member_added']): void {
    this.track('federal_tax_member_added', props)
  }

  // ─── EIN Correction ──────────────────────────────────────────

  einCorrectionStarted(props: AnalyticsEventMap['ein_correction_started']): void {
    this.track('ein_correction_started', props)
  }

  phoneVerificationDeferred(props: AnalyticsEventMap['phone_verification_deferred']): void {
    this.track('phone_verification_deferred', props)
  }

  einCorrectionStepViewed(props: AnalyticsEventMap['ein_correction_step_viewed']): void {
    this.track('ein_correction_step_viewed', props)
  }

  einCorrectionStepCompleted(props: AnalyticsEventMap['ein_correction_step_completed']): void {
    this.track('ein_correction_step_completed', props)
  }

  // ─── Payment Success ─────────────────────────────────────────

  // Payment Success Viewed → purchase (item_category: 'formation')
  paymentSuccessViewed(props: AnalyticsEventMap['purchase']): void {
    this.track('purchase', { ...props, item_category: props.item_category || 'formation' })
  }

  // The counterpart to checkoutStarted (`begin_checkout`). `purchase` above is
  // shaped for the ad platforms; this is the funnel event that gives
  // begin_checkout a denominator, so a checkout that never settles stops
  // looking like a checkout that was never attempted.
  checkoutCompleted(props: AnalyticsEventMap['checkout_completed']): void {
    this.track('checkout_completed', props)
  }

  paymentProcessingDelayed(props: AnalyticsEventMap['payment_processing_delayed']): void {
    this.track('payment_processing_delayed', props)
  }

  paymentProcessingFailed(props: AnalyticsEventMap['payment_processing_failed']): void {
    this.track('payment_processing_failed', props)
  }

  paymentSetupRetried(props: AnalyticsEventMap['payment_setup_retried']): void {
    this.track('payment_setup_retried', props)
  }
}

export const analytics = new AnalyticsService()
